/** biome-ignore-all lint/complexity/noExcessiveLinesPerFunction: too many lines */

import {useCallback} from 'react';
import {shallowEqual, useSelector, useStore} from 'react-redux';
import type {Action, UnknownAction} from 'redux';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {useAutoScrollers} from '~/CreateFabricInvitation/hooks/useAutoScrollers';
import {navigateToRoute, useCurrentRoute} from '~/CreateFabricInvitation/navigation';
import {
  selectIsEditingDesign,
  setIsEditingDesign,
} from '~/CreateFabricInvitation/reducers/preview/reducer';
import {webViewPendingUpgradeToPremium} from '~/CreateFabricInvitation/webview/webview';
import {
  selectFeatureToggling,
  selectInAdsToggle,
  selectShowCharityController,
  selectShowCrowdfundController,
  selectShowUpsellModal,
  selectUpsellMode,
  setFeatureToggling,
  setInAdsToggle,
  setShowCharityController,
  setShowCrowdfundController,
  setShowUpsellModal,
  setUpsellMode,
} from '~/common/event/controllers/reducer';
import {
  selectIsPremium,
  selectIsPremiumTemplate,
  selectIsProOrPartyPass,
} from '~/common/FabricInvitation/reducers/fabricTemplate/selectors';
import {webViewModalShowing} from '~/common/fabric/hooks';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {eventInEditMode} from '~/common/fabric/reducers/details/selectors';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {toggleOption} from '~/common/fabric/reducers/giftingOptions/actions';
import {UPDATE_GIFT_OPTIONS} from '~/common/fabric/reducers/giftingOptions/constants';
import {
  selectGiftingOptions,
  selectGiftOptionsCountInComponent,
  selectHostPhotoGalleryEnabled,
  selectPhotosEnabled,
  selectShowGifting,
} from '~/common/fabric/reducers/giftingOptions/selectors';
import {SET_UPSELL_CONFIRMED} from '~/common/fabric/reducers/modals';
import {UPSELL_MODES} from '~/common/fabric/UpsellModal/UpsellModal';
import {EventFeature} from '~/common/types/event';
import {TOGGLE_HOST_GALLERY_EDITOR_OPEN} from '~/ViewInvitation/reducers/constants';
import {initPremiumDesign} from '~/ViewInvitation/reducers/premiumDesign';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';

export const useFeatureControls = () => {
  const current = useCurrentRoute();
  const store = useStore();
  const isDesktop = useIsLargishAndUp();
  const {scrollElementIntoViewById} = useAutoScrollers();

  const featureToggling = useSelector(selectFeatureToggling);
  const upsellMode = useSelector(selectUpsellMode);
  const inAdsToggle = useSelector(selectInAdsToggle);
  const showCharityController = useSelector(selectShowCharityController);
  const showCrowdfundController = useSelector(selectShowCrowdfundController);
  const showUpsellModal = useSelector(selectShowUpsellModal);
  const inEditMode = useSelector(eventInEditMode);
  const isPremium = useSelector(selectIsPremium);
  const showGifting = useSelector(selectShowGifting);
  const giftingOptions = useSelector(selectGiftingOptions, shallowEqual);
  const isPhotosEnabled: boolean = useSelector(selectPhotosEnabled);
  const isHostPhotoGalleryEnabled: boolean = useSelector(selectHostPhotoGalleryEnabled);
  const isPremiumTemplate = useSelector(selectIsPremiumTemplate);
  const isProOrPartyPass = useSelector(selectIsProOrPartyPass);
  const giftOptionsCount = useSelector(selectGiftOptionsCountInComponent);
  const isEditingDesign = useSelector(selectIsEditingDesign);

  // [] syntax in case we add more upsells with these features
  const upsellHasBannerImg = Boolean(
    featureToggling && [EventFeature.HOST_PHOTO_GALLERY].includes(featureToggling)
  );
  const upsellUsesAccordion = Boolean(
    featureToggling && [EventFeature.HOST_PHOTO_GALLERY].includes(featureToggling)
  );
  const photoFeatureToggled = Boolean(
    featureToggling &&
      [EventFeature.PHOTO_SHARING, EventFeature.HOST_PHOTO_GALLERY].includes(featureToggling)
  );

  const setPhotoSharing = useCallback(
    (enabled: boolean) => {
      // If turning off, scroll down to it before removing
      if (!enabled) scrollElementIntoViewById('module-photo', {delay: 0});
      store.dispatch(toggleOption(giftingOptions, 'event_option_enable_photo_sharing', enabled));
      // If just turned on, scroll down to it after turning it on
      if (enabled) scrollElementIntoViewById('module-photo');
    },
    [store, giftingOptions, scrollElementIntoViewById]
  );

  const enableHostPhotoGallery = useCallback(
    (enabled: boolean) => {
      // If turning off, scroll down to it before removing
      if (!enabled) scrollElementIntoViewById('module-host_photo', {delay: 0});
      store.dispatch(
        toggleOption(giftingOptions, 'event_option_enable_host_photo_gallery', enabled)
      );
      // If just turned on, scroll down to it after turning it on
      if (enabled) scrollElementIntoViewById('module-host_photo');
    },
    [store, giftingOptions, scrollElementIntoViewById]
  );

  const removeGifting = useCallback(async () => {
    store.dispatch({
      type: UPDATE_GIFT_OPTIONS,
      payload: {
        showGifting: false,
        options: {
          ...giftingOptions.options,
          // biome-ignore lint/style/useNamingConvention: existing error
          event_option_show_gifting: false,
        },
      },
    });
  }, [store, giftingOptions]);

  const toggleUpsellModal = useCallback(() => {
    const prev = showUpsellModal;
    if (!prev) {
      webViewModalShowing('upsell');
    } else {
      store.dispatch(setUpsellMode(undefined));
    }
    store.dispatch(setShowUpsellModal(!prev));
  }, [store, showUpsellModal]);

  const onConfirmOrDeny = useCallback(async () => {
    if (!inAdsToggle && !photoFeatureToggled) {
      store.dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {savingStep: true},
      });
    }

    store.dispatch(saveEventThunk(!inEditMode) as unknown as Action);
    webViewPendingUpgradeToPremium(true);
    if (!inAdsToggle && !photoFeatureToggled) {
      window.dataLayer.push({
        event: 'fabric_create_upgrade_ads_toggle',
      });
      store.dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {savingStep: false},
      });
    }

    store.dispatch(setInAdsToggle(false));
  }, [inAdsToggle, photoFeatureToggled, store, inEditMode]);

  const upsellConfirm = useCallback(async () => {
    store.dispatch({
      type: SET_UPSELL_CONFIRMED,
      payload: true,
    });
    store.dispatch({
      type: UPDATE_EVENT_DETAILS,
      payload: {isPremium: true, hideUpsell: true},
    });
    store.dispatch({
      type: UPDATE_GIFT_OPTIONS,
      payload: {
        upgraded: true,
      },
    });

    switch (featureToggling) {
      case EventFeature.PHOTO_SHARING:
      case EventFeature.ADS:
        setPhotoSharing(true);
        break;
      case EventFeature.HOST_PHOTO_GALLERY:
        enableHostPhotoGallery(true);
        if (isDesktop) {
          store.dispatch({type: TOGGLE_HOST_GALLERY_EDITOR_OPEN, payload: true});
        }
        break;
      case EventFeature.GIFT_IDEAS:
        await removeGifting();
        break;
      case EventFeature.PREMIUM_DESIGN_TOOLS:
        store.dispatch(setIsEditingDesign(false));
        break;
      default:
        break;
    }
    toggleUpsellModal();

    await onConfirmOrDeny();

    store.dispatch(setInAdsToggle(false));
  }, [
    store,
    enableHostPhotoGallery,
    featureToggling,
    onConfirmOrDeny,
    toggleUpsellModal,
    setPhotoSharing,
    removeGifting,
    isDesktop,
  ]);

  const onToggleUpsellModal = useCallback(async () => {
    if (featureToggling === EventFeature.ADS) {
      toggleUpsellModal();
      if (!inAdsToggle) await onConfirmOrDeny();
      store.dispatch(setInAdsToggle(!inAdsToggle));
    } else if (featureToggling === EventFeature.PHOTO_SHARING) {
      if (isPremium) {
        setPhotoSharing(true);
      }
      toggleUpsellModal();
    } else if (featureToggling === EventFeature.HOST_PHOTO_GALLERY) {
      if (isPremium) {
        enableHostPhotoGallery(true);
      }
      toggleUpsellModal();
    }
    store.dispatch(setFeatureToggling(null));
  }, [
    featureToggling,
    inAdsToggle,
    isPremium,
    onConfirmOrDeny,
    toggleUpsellModal,
    setPhotoSharing,
    enableHostPhotoGallery,
    store,
  ]);

  const onCloseUpsellOverlay = useCallback(async () => {
    if (featureToggling === EventFeature.ADS) {
      if (!inAdsToggle) {
        store.dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {savingStep: true},
        });
      }

      webViewPendingUpgradeToPremium(false);

      if (!inAdsToggle) {
        await navigateToRoute(current.nextRoute, store);
      }

      store.dispatch(setInAdsToggle(false));
    } else if (featureToggling === EventFeature.PREMIUM_DESIGN_TOOLS && isEditingDesign) {
      // Reset premium design choices
      if (window.evite_event) {
        store.dispatch(initPremiumDesign({premiumDesign: window.evite_event.options.design}));
      }
      store.dispatch({
        type: UPDATE_DESIGN_OPTIONS,
        payload: {
          usedPremiumFeature: false,
        },
      });
      // Re-sync latest state of card design
      await store.dispatch(saveEventThunk(false) as unknown as UnknownAction);
      // Close the design editor
      store.dispatch(setIsEditingDesign(false));
    } else if (photoFeatureToggled) {
      webViewPendingUpgradeToPremium(false);
    }
    store.dispatch(setFeatureToggling(null));
    toggleUpsellModal();
  }, [
    current?.nextRoute,
    featureToggling,
    photoFeatureToggled,
    inAdsToggle,
    store,
    toggleUpsellModal,
    isEditingDesign,
  ]);

  const addGifting = useCallback(async () => {
    store.dispatch({
      type: UPDATE_GIFT_OPTIONS,
      payload: {
        showGifting: true,
        options: {
          ...giftingOptions.options,
          // biome-ignore lint/style/useNamingConvention: existing error
          event_option_show_gifting: true,
        },
      },
    });
    scrollElementIntoViewById('submodule-gifting');
  }, [store, giftingOptions, scrollElementIntoViewById]);

  const removeAds = useCallback(() => {
    store.dispatch(setInAdsToggle(true));
    store.dispatch(setFeatureToggling(EventFeature.ADS));
    store.dispatch(setUpsellMode(UPSELL_MODES.REMOVE_ADS));
    toggleUpsellModal();
  }, [toggleUpsellModal, store]);

  const unlockDesignTools = useCallback(() => {
    store.dispatch(setFeatureToggling(EventFeature.PREMIUM_DESIGN_TOOLS));
    store.dispatch(setUpsellMode(UPSELL_MODES.UNLOCK_DESIGN_TOOLS));
    toggleUpsellModal();
  }, [toggleUpsellModal, store]);

  const handlePremiumFeatureToggle = useCallback(
    (featureToToggle: EventFeature) => {
      switch (featureToToggle) {
        case EventFeature.PHOTO_SHARING: {
          setPhotoSharing(!isPhotosEnabled);
          store.dispatch(setFeatureToggling(EventFeature.PHOTO_SHARING));
          break;
        }
        case EventFeature.HOST_PHOTO_GALLERY: {
          if (!isHostPhotoGalleryEnabled || !isDesktop) {
            // Host Photo Gallery is off, or we're on MW which only has on/off for the feature
            if (isPremium || isHostPhotoGalleryEnabled) {
              // If premium invitation or host gallery is on, toggle the feature
              enableHostPhotoGallery(!isHostPhotoGalleryEnabled);
              if (isDesktop && !isHostPhotoGalleryEnabled) {
                // If on desktop and host gallery is off, also open the editor
                store.dispatch({type: TOGGLE_HOST_GALLERY_EDITOR_OPEN, payload: true});
              }
            } else {
              // Free invitation; show Premium upsell
              store.dispatch(setFeatureToggling(EventFeature.HOST_PHOTO_GALLERY));
              store.dispatch(setUpsellMode(UPSELL_MODES.HOST_PHOTO_GALLERY));
              toggleUpsellModal();
            }
          } else if (isDesktop) {
            // Host Photo Gallery is on and we're on desktop, so open the editor
            store.dispatch({type: TOGGLE_HOST_GALLERY_EDITOR_OPEN, payload: true});
          }
          break;
        }
        case EventFeature.GIFT_IDEAS: {
          if (showGifting) {
            const shouldShowUpsellModal =
              !isPremiumTemplate && !isPremium && !isProOrPartyPass && giftOptionsCount === 1;
            if (shouldShowUpsellModal) {
              store.dispatch(setFeatureToggling(EventFeature.GIFT_IDEAS));
              store.dispatch(setUpsellMode(UPSELL_MODES.GIFTING));
              toggleUpsellModal();
            } else {
              removeGifting();
            }
          } else {
            addGifting();
          }
          break;
        }
        default:
          break;
      }
    },
    [
      enableHostPhotoGallery,
      isHostPhotoGalleryEnabled,
      isDesktop,
      store,
      isPremiumTemplate,
      isPremium,
      isProOrPartyPass,
      showGifting,
      giftOptionsCount,
      isPhotosEnabled,
      setPhotoSharing,
      toggleUpsellModal,
      removeGifting,
      addGifting,
    ]
  );

  const handleFeatureClick = useCallback(
    (feature: EventFeature) => {
      switch (feature) {
        case EventFeature.HOST_PHOTO_GALLERY: {
          handlePremiumFeatureToggle(EventFeature.HOST_PHOTO_GALLERY);
          break;
        }
        case EventFeature.PHOTO_SHARING: {
          handlePremiumFeatureToggle(EventFeature.PHOTO_SHARING);
          break;
        }
        case EventFeature.CHARITY: {
          store.dispatch(setShowCharityController(!showCharityController));
          break;
        }
        case EventFeature.CROWDFUNDING: {
          store.dispatch(setShowCrowdfundController(!showCrowdfundController));
          break;
        }
        default:
          // Logic is already handled by the Component attribute for the feature
          break;
      }
    },
    [handlePremiumFeatureToggle, store, showCharityController, showCrowdfundController]
  );

  return {
    featureToggling,
    setFeatureToggling,
    showUpsellModal,
    setShowUpsellModal,
    upsellMode,
    setUpsellMode,
    upsellHasBannerImg,
    upsellUsesAccordion,
    isPhotosEnabled,
    isHostPhotoGalleryEnabled,
    isPremium,
    onToggleUpsellModal,
    onCloseUpsellOverlay,
    upsellConfirm,
    setPhotoSharing,
    enableHostPhotoGallery,
    handlePremiumFeatureToggle,
    removeAds,
    unlockDesignTools,
    handleFeatureClick,
    showCharityController,
    showCrowdfundController,
  };
};
